import React from "react";
import * as Yup from "yup";
import FormBuilder from "../../../../../../../shared/components/form-builder/form-builder";
import { ITcProductComponentInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";

interface ComponentInfoProps {
  formRef: React.MutableRefObject<any>;
  componentInfoData: ITcProductComponentInformation;
  setComponentInfoData: (data: ITcProductComponentInformation) => void;
}

const disallowedPlaceholderValues = new Set(["na", "n/a", "not available", "null"]);

const isBlankOrPlaceholder = (value: unknown) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return !normalized || disallowedPlaceholderValues.has(normalized);
};

/**
 * C-Step 1 — Component Information.
 * Required: tcProductComponentNo, tcProductComponentDetail, tcProductComponentNetShippingWeight.
 * Optional: fiber length / yarn count / fabric weight + construct / fabric weight of finished
 * product / technical specifications / additional. Mirrors the product-info FormBuilder pattern;
 * the parent component modal drives validateForm/submitForm via the ref.
 */
const ComponentInfo: React.FC<ComponentInfoProps> = ({
  formRef,
  componentInfoData,
  setComponentInfoData,
}) => {
  const validationSchema = Yup.object().shape({
    tcProductComponentNo: Yup.string()
      .required("Component Number is required")
      .test(
        "not-blank-or-placeholder",
        "Component Number cannot be blank, na, n/a, not available, or null",
        (value: string | undefined) => !isBlankOrPlaceholder(value)
      )
      .max(50, "Maximum 50 characters allowed"),
    tcProductComponentDetail: Yup.string()
      .required("Component Detail is required")
      .test(
        "not-blank-or-placeholder",
        "Component Detail cannot be blank, na, n/a, not available, or null",
        (value: string | undefined) => !isBlankOrPlaceholder(value)
      )
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentNetShippingWeight: Yup.string()
      .required("Component Net Shipping Weight is required")
      .test(
        "is-max-five-decimal-places",
        "Component Net Shipping Weight must be a valid decimal number with up to 5 decimal places.",
        (value: string | undefined) => {
          if (value === undefined || value === null || value === "") return false;
          return /^(0|[0-9]\d*|0\.\d{1,5}|[0-9]\d*\.\d{1,5})$/.test(String(value));
        }
      )
      .test(
        "greater-than-zero",
        "Component Net Shipping Weight must be greater than 0",
        (value: string | undefined) => {
          const num = parseFloat(String(value));
          return !isNaN(num) && num > 0;
        }
      ),
    tcProductComponentInfoFiberLength: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoYarnCount: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoFabricWeightAndConstruct: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoFabricWeightOfFinishedProduct: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoTechnicalSpecifications: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoAdditional: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
  });

  const formConfig = {
    pageName: "certified-products-component",
    subPageName: "tcProductComponentInformation",
    initialValues: componentInfoData,
    enableReinitialize: true,
    validationSchema,
    sections: [
      {
        title: "Component Information",
        fields: [
          {
            label: "Component Number",
            name: "tcProductComponentNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: componentInfoData.tcProductComponentNo,
            props: { placeholder: "Enter component number" },
          },
          {
            label: "Component Detail",
            name: "tcProductComponentDetail",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: componentInfoData.tcProductComponentDetail,
            props: { placeholder: "Enter component detail" },
          },
          {
            label: "Component Net Shipping Weight",
            name: "tcProductComponentNetShippingWeight",
            type: "number",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: componentInfoData.tcProductComponentNetShippingWeight,
            props: { type: "number", placeholder: "Enter component net shipping weight" },
          },
          {
            label: "Component Info Fiber Length (mm)",
            name: "tcProductComponentInfoFiberLength",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: componentInfoData.tcProductComponentInfoFiberLength,
            props: { placeholder: "Enter fiber length" },
          },
          {
            label: "Component Info Yarn Count",
            name: "tcProductComponentInfoYarnCount",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: componentInfoData.tcProductComponentInfoYarnCount,
            props: { placeholder: "Enter yarn count" },
          },
          {
            label: "Component Info Fabric Weight and Construct",
            name: "tcProductComponentInfoFabricWeightAndConstruct",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: componentInfoData.tcProductComponentInfoFabricWeightAndConstruct,
            props: { placeholder: "Enter fabric weight and construct" },
          },
          {
            label: "Component Info Fabric Weight of Finished Product",
            name: "tcProductComponentInfoFabricWeightOfFinishedProduct",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: componentInfoData.tcProductComponentInfoFabricWeightOfFinishedProduct,
            props: { placeholder: "Enter fabric weight of finished product" },
          },
          {
            label: "Component Info Technical Specifications",
            name: "tcProductComponentInfoTechnicalSpecifications",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: componentInfoData.tcProductComponentInfoTechnicalSpecifications,
            props: { placeholder: "Enter technical specifications" },
          },
          {
            label: "Component Info Additional",
            name: "tcProductComponentInfoAdditional",
            type: "text",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            value: componentInfoData.tcProductComponentInfoAdditional,
            props: { placeholder: "Enter additional component info" },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setComponentInfoData(values);
    },
  };

  return (
    <FormBuilder
      formConfig={formConfig}
      onSubmit={formConfig.handleSubmit}
      formRef={formRef}
    />
  );
};

export default ComponentInfo;
